import { Box, Button, Card, Divider, Stack, TextField, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { BasicInfoPiece, BasicInfoRow } from "../backups/BackupBasicInfoCard";
import WithSave from "@/controller/WithSave";
import { DatabaseResponseDto } from "@/gen/api/governance";
import { useShowContext } from "ra-core";
import { StateErrors, StateTouched, usePlainStateFormUtils } from "../input/usePlainStateFormUtils";
import * as Yup from 'yup';
import { useHasPermit } from "@/authProvider/useHasPermit";
import { MANAGE_DATA_CATALOG } from "@/constants";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import CreateOrEdit from "@/controller/CreateOrEdit";



const dbSemanticRes = 'governance/database-semantic'

const validationSchema =
  Yup.object().shape({
    description: Yup.string().max(512),
  })

const initialS = (database?: DatabaseResponseDto) => ({
  prevData: database,
  stage: {
    description: database?.description ?? '',
  },
  errors: {} as StatesError,
  touched: {} as StatesTouched,
})

const DatabaseSemanticEditCard: FC<{
  database : DatabaseResponseDto,
  refetch?: ()=>void,
}> = ({
  database, refetch,
}) => {
  const hasWritePermit = useHasPermit([MANAGE_DATA_CATALOG]);
  
  const { loading } = useShowContext({
    resource: dbSemanticRes,
  });
  const [states, setStates] = useState(initialS(database))
  const hasChanges = Object.values(states.touched).some(x => x)
  useEffect(() => {
    if (states.prevData?.databaseId !== database?.databaseId) {
      setStates(initialS(database))
    }
  }, [database, states.prevData])
  
  const { trySubmitValidate, fieldProps } = usePlainStateFormUtils(states, setStates, validationSchema)

  const handlers = useDefaultSavedHandlers('Save Database Semantic Info')
  const handleSave = (save: (x: any) => Promise<void>) => async () => {
    if (!states.touched.description || states.prevData?.description === states.stage.description || loading) { return }
    if (!hasWritePermit || !database) return;

    const payload = await trySubmitValidate()
    await save({...payload.stage, databaseId: database.databaseId })
    setStates(v => ({...v,
      errors: {},
      touched: {},
    }))
    refetch?.()
  };
  return (
    <Card>
      <BasicInfoRow>
        <BasicInfoPiece>
          <Typography color="textPrimary" variant="h6" >
            Database
          </Typography>
          <Typography color="textSecondary" variant="body1" sx={{
            maxWidth: 450, overflowWrap: 'break-word', textWrap: 'wrap'
          }}>
            {database.name}
          </Typography>
        </BasicInfoPiece>
        <BasicInfoPiece>
          <Typography color="textPrimary" variant="h6" >
            Engine
          </Typography>
          <Typography color="textSecondary" variant="body1" >
            {database?.engineVersion + ' ' + (database?.engineEdition || '')}
          </Typography>
        </BasicInfoPiece>
      </BasicInfoRow>
      <Divider />
      {database?.comment &&
        <Stack direction="row" sx={{ mx: 3, my: 2 }} spacing={4}  >        
          <Typography color="textPrimary" variant="h6"
            id="database-comment-label" 
            sx={{ width: 90 }}
          >
            Comment
          </Typography>
          <Stack direction="row" flex="1 1 0" alignItems="center">
            <Typography variant="body2" noWrap={false} >
              {database?.comment}
            </Typography>
          </Stack>
        </Stack>}
      <Stack direction="row" sx={{ mx: 3, my: 2 }} spacing={4}>        
        <Typography color="textPrimary" variant="h6"
          id="database-description-label" 
          sx={{ flex: 1 }}
        >
          Description
        </Typography>
        <TextField multiline fullWidth minRows={3} maxRows={8}
          inputProps={{
            "aria-labelledby": "database-description-label"
          }}
          {...fieldProps('description')}
          disabled={!hasWritePermit}
        />
      </Stack>
      {hasChanges && (
        <Box sx={{ mt: 2, mr: 3, mb: 2, display: 'flex', gap: 1 }}>
          <CreateOrEdit resource={dbSemanticRes} basePath="" id={database.name ?? ''} record={database} {...handlers} >
            <WithSave async render={({ save, saving }) =>
              <>
                <Button sx={{ ml: 'auto' }}
                  onClick={() => {
                    setStates(v => ({...v, 
                      stage: {...v.stage, description: v.prevData?.description ?? ''}, 
                      errors: {}, 
                      touched: {}
                    }))
                  }}
                  disabled={!hasChanges || saving}
                >
                  Cancel
                </Button>
                <Button variant="contained"
                  onClick={handleSave(save)}
                  disabled={!hasWritePermit || saving}
                >
                  Save
                </Button>
              </>
            } />
          </CreateOrEdit>  
        </Box>
      )}
    </Card>
  )
}

export default DatabaseSemanticEditCard;

interface UIState {
  description: string;
}
type StatesError = StateErrors<UIState>;
type StatesTouched = StateTouched<UIState>;
